import { arrayOfComments } from "~/schemas/schema.comments.jsx";
import * as schemaNotes from "~/schemas/schema.notes.jsx";
import * as actionTypes from "~/store/action_types";
import Angie from "~/utils/angie.jsx";
import { fetch } from "~/utils/fetch.jsx";
import { encodeData } from "~/utils/utils";
import * as trashActions from "./actions.trash.jsx";

export const fetchNote = (projectId, noteId) => {
  const url = `${Angie.api_url}/projects/${projectId}/notes/${noteId}`;
  return {
    types: [actionTypes.FETCH_NOTE_LOADING, actionTypes.FETCH_NOTE_SUCCESS, actionTypes.FETCH_NOTE_ERROR],
    callAPI: () =>
      fetch({
        url,
      }),
    payload: {
      projectId,
      noteId,
      schema: {
        single: schemaNotes.note,
        comments: arrayOfComments,
      },
    },
    cache: {
      key: url,
    },
  };
};

export const prefetchNote = (projectId, noteId) => {
  return {
    types: [actionTypes.PREFETCH_NOTE_LOADING, actionTypes.PREFETCH_NOTE_SUCCESS, actionTypes.PREFETCH_NOTE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/${noteId}`,
        prefetchNext: true,
      }),
    payload: {
      projectId,
      noteId,
      schema: {
        single: schemaNotes.note,
        comments: arrayOfComments,
      },
    },
  };
};

export const addNote = (projectId, attributes) => {
  return {
    types: [actionTypes.ADD_NOTE_LOADING, actionTypes.ADD_NOTE_SUCCESS, actionTypes.ADD_NOTE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes`,
        method: "POST",
        body: attributes,
      }),
    payload: {
      projectId,
      attributes,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const editNote = (projectId, noteId, attributes) => {
  return {
    types: [actionTypes.EDIT_NOTE_LOADING, actionTypes.EDIT_NOTE_SUCCESS, actionTypes.EDIT_NOTE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/${noteId}`,
        method: "PUT",
        body: attributes,
      }),
    payload: {
      projectId,
      noteId,
      attributes,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const moveToTrash = (id) => {
  const objectType = `Note`;
  return {
    ...trashActions.moveToTrash(objectType, id),
    types: [
      actionTypes.MOVE_NOTE_TO_TRASH_LOADING,
      actionTypes.MOVE_NOTE_TO_TRASH_SUCCESS,
      actionTypes.MOVE_NOTE_TO_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const restoreFromTrash = (id) => {
  const objectType = `Note`;
  return {
    ...trashActions.restoredFromTrash(objectType, id),
    types: [
      actionTypes.RESTORE_NOTE_FROM_TRASH_LOADING,
      actionTypes.RESTORE_NOTE_FROM_TRASH_SUCCESS,
      actionTypes.RESTORE_NOTE_FROM_TRASH_ERROR,
    ],
    payload: {
      objectType,
      id,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const fetchNotes = (projectId, page = 1, sortBy = "updated", order = "desc", signal) => {
  let query = encodeData({ page, sort: sortBy, order });

  if (query) {
    query = "?" + query;
  }

  return {
    types: [actionTypes.FETCH_NOTES_LOADING, actionTypes.FETCH_NOTES_SUCCESS, actionTypes.FETCH_NOTES_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/for-screen${query}`,
        withHeaders: true,
        signal,
      }),
    payload: {
      projectId,
      page,
      sortBy,
      order,
      schema: schemaNotes.arrayOfNotes,
    },
  };
};

export const addNoteFromEventStream = (data) => {
  return {
    type: actionTypes.ADD_NOTE_FROM_EVENT_STREAM,
    response: data,
    schema: schemaNotes.note,
  };
};

export const updateNoteFromEventStream = (data) => {
  return {
    type: actionTypes.UPDATE_NOTE_FROM_EVENT_STREAM,
    response: data,
    schema: schemaNotes.note,
  };
};

export const moveNoteToTrashFromEventStream = (data) => {
  return {
    type: actionTypes.MOVE_NOTE_TO_TRASH_FROM_EVENT_STREAM,
    response: data,
    schema: schemaNotes.note,
  };
};

export const restoreNoteFromTrashFromEventStream = (data) => {
  return {
    type: actionTypes.RESTORE_NOTE_FROM_TRASH_FROM_EVENT_STREAM,
    response: data,
    schema: schemaNotes.note,
  };
};

export const noteMovedToProjectFromEventStream = (data) => {
  return {
    type: actionTypes.NOTE_MOVED_TO_PROJECT_FROM_EVENT_STREAM,
    response: data,
    schema: schemaNotes.note,
  };
};

export const fetchNotebookNotes = (projectId, notebookId, page = 1, sortBy = "updated", order = "desc", signal) => {
  let query = encodeData({ page, sort: sortBy, order });

  if (query) {
    query = "?" + query;
  }

  return {
    types: [
      actionTypes.FETCH_NOTEBOOK_NOTES_LOADING,
      actionTypes.FETCH_NOTEBOOK_NOTES_SUCCESS,
      actionTypes.FETCH_NOTEBOOK_NOTES_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notebooks/${notebookId}/for-screen${query}`,
        withHeaders: true,
        signal,
      }),
    payload: {
      projectId,
      notebookId,
      page,
      sortBy,
      order,
      schema: schemaNotes.arrayOfNotes,
    },
  };
};

export const moveCopyNoteToProject = (projectId, id, attributes) => {
  return {
    types: [
      actionTypes.MOVE_NOTE_TO_PROJECT_LOADING,
      actionTypes.MOVE_NOTE_TO_PROJECT_SUCCESS,
      actionTypes.MOVE_NOTE_TO_PROJECT_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/${id}/move-to-project`,
        method: "put",
        body: attributes,
      }),
    payload: {
      projectId,
      noteId: id,
      copy: attributes.copy,
      newProjectId: attributes.project_id,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const clearNotes = (projectId) => {
  return {
    type: actionTypes.CLEAR_NOTES,
    payload: {
      projectId,
    },
  };
};
export const revertNoteToOriginal = (projectId, noteId) => {
  return {
    types: [
      actionTypes.REVERT_NOTE_TO_ORIGINAL_LOADING,
      actionTypes.REVERT_NOTE_TO_ORIGINAL_SUCCESS,
      actionTypes.REVERT_NOTE_TO_ORIGINAL_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/${noteId}/revert-to-original`,
        method: "PUT",
      }),
    payload: {
      projectId,
      noteId,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const revertNoteToModification = (projectId, noteId, modificationId) => {
  return {
    types: [
      actionTypes.REVERT_NOTE_TO_MODIFICATION_LOADING,
      actionTypes.REVERT_NOTE_TO_MODIFICATION_SUCCESS,
      actionTypes.REVERT_NOTE_TO_MODIFICATION_ERROR,
    ],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/${noteId}/revert-to-modification`,
        method: "PUT",
        body: { modification_id: modificationId },
      }),
    payload: {
      projectId,
      noteId,
      modificationId,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};

export const duplicateNote = (projectId, id, attributes) => {
  return {
    types: [actionTypes.DUPLICATE_NOTE_LOADING, actionTypes.DUPLICATE_NOTE_SUCCESS, actionTypes.DUPLICATE_NOTE_ERROR],
    callAPI: () =>
      fetch({
        url: `${Angie.api_url}/projects/${projectId}/notes/${id}/duplicate`,
        method: `post`,
        body: attributes,
      }),
    payload: {
      projectId,
      noteId: id,
      schema: {
        single: schemaNotes.note,
      },
    },
  };
};
